/**
 * PortfolioExample - Static mock portfolio dashboard for the landing page
 *
 * Self-contained component with hardcoded data. No API dependencies.
 * Showcases the Portfolio Intelligence feature's key visuals:
 * summary cards, needs-attention alerts, holdings table with expandable
 * bull/bear thesis, key financial metrics, and position details.
 */

import { useState, Fragment } from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, ChevronDown, Zap, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Mock data ───────────────────────────────────────────────────────────

type Stance = 'BULLISH' | 'LEANING_BULLISH' | 'NEUTRAL' | 'LEANING_BEARISH' | 'BEARISH';

interface MockMetrics {
  pe: string;
  pb: string;
  ps: string;
  evEbitda: string;
  roe: string;
  divYield: string;
}

interface MockHolding {
  symbol: string;
  name: string;
  quantity: number;
  avgCost: number;
  currentPrice: number;
  currentValue: number;
  gainLossPct: number;
  gainLoss: number;
  stance: Stance;
  bullIntensity: number;
  bearRisk: number;
  bullThesis: string;
  bearThesis: string;
  catalysts: string[];
  risks: string[];
  sparkline: number[];
  needsAttention: boolean;
  metrics: MockMetrics;
}

const MOCK_HOLDINGS: MockHolding[] = [
  {
    symbol: 'NVDA',
    name: 'NVIDIA Corporation',
    quantity: 45,
    avgCost: 96.77,
    currentPrice: 138.25,
    currentValue: 6221.25,
    gainLossPct: 42.8,
    gainLoss: 1866.50,
    stance: 'BULLISH',
    bullIntensity: 78,
    bearRisk: 35,
    bullThesis: 'Dominant AI infrastructure position with 80%+ data center GPU market share. Blackwell architecture ramping with unprecedented demand from hyperscalers.',
    bearThesis: 'Extreme valuation at 52x earnings prices in perfection. Customer concentration risk and potential in-house chip development by major cloud providers.',
    catalysts: ['Blackwell ramp', 'Sovereign AI deals'],
    risks: ['China export controls', 'Customer ASICs'],
    sparkline: [90, 95, 92, 98, 105, 110, 108, 115, 125, 130, 128, 138],
    needsAttention: false,
    metrics: { pe: '64.2', pb: '42.8', ps: '35.1', evEbitda: '52.3', roe: '115.7%', divYield: '0.03%' },
  },
  {
    symbol: 'INTC',
    name: 'Intel Corporation',
    quantity: 200,
    avgCost: 34.05,
    currentPrice: 22.40,
    currentValue: 4480.00,
    gainLossPct: -34.2,
    gainLoss: -2330.00,
    stance: 'BEARISH',
    bullIntensity: 22,
    bearRisk: 82,
    bullThesis: 'Government CHIPS Act subsidies provide capital for foundry buildout. 18A process node shows competitive potential if execution improves.',
    bearThesis: 'Continued market share losses in data center and client. Foundry strategy burning cash with no clear path to profitability. Management credibility gap.',
    catalysts: ['18A milestone'],
    risks: ['Foundry cash burn', 'Share loss to AMD'],
    sparkline: [45, 42, 38, 35, 30, 28, 25, 23, 22, 21, 22, 22],
    needsAttention: true,
    metrics: { pe: 'N/A', pb: '0.8', ps: '1.6', evEbitda: '18.4', roe: '-1.2%', divYield: '1.62%' },
  },
  {
    symbol: 'AAPL',
    name: 'Apple Inc.',
    quantity: 30,
    avgCost: 196.20,
    currentPrice: 232.50,
    currentValue: 6975.00,
    gainLossPct: 18.5,
    gainLoss: 1090.00,
    stance: 'LEANING_BULLISH',
    bullIntensity: 62,
    bearRisk: 38,
    bullThesis: 'Services revenue growing at 15%+ with 2B+ active devices. Apple Intelligence rollout could drive upgrade supercycle.',
    bearThesis: 'iPhone unit growth stagnating in key markets. China competition intensifying from Huawei. AI features lagging competitors.',
    catalysts: ['AI upgrade cycle', 'Vision Pro V2'],
    risks: ['China slowdown', 'Regulatory pressure'],
    sparkline: [195, 200, 205, 210, 215, 220, 218, 225, 228, 230, 231, 232],
    needsAttention: false,
    metrics: { pe: '33.1', pb: '52.4', ps: '8.8', evEbitda: '26.7', roe: '157.4%', divYield: '0.44%' },
  },
  {
    symbol: 'SOFI',
    name: 'SoFi Technologies',
    quantity: 350,
    avgCost: 16.28,
    currentPrice: 14.20,
    currentValue: 4970.00,
    gainLossPct: -12.8,
    gainLoss: -728.00,
    stance: 'LEANING_BEARISH',
    bullIntensity: 35,
    bearRisk: 65,
    bullThesis: 'Bank charter enables lower funding costs. Member growth accelerating with 8M+ users. Galileo platform provides B2B upside.',
    bearThesis: 'Still unprofitable on GAAP basis. Rising rate environment pressures lending margins. Student loan restart headwinds.',
    catalysts: ['GAAP profitability'],
    risks: ['Credit deterioration', 'Rate sensitivity'],
    sparkline: [18, 17, 16, 15, 14.5, 14, 13.5, 13.8, 14, 14.2, 14.1, 14.2],
    needsAttention: true,
    metrics: { pe: 'N/A', pb: '1.9', ps: '4.2', evEbitda: 'N/A', roe: '-2.8%', divYield: '\u2014' },
  },
  {
    symbol: 'MSFT',
    name: 'Microsoft Corporation',
    quantity: 15,
    avgCost: 331.80,
    currentPrice: 415.80,
    currentValue: 6237.00,
    gainLossPct: 25.3,
    gainLoss: 1260.00,
    stance: 'BULLISH',
    bullIntensity: 75,
    bearRisk: 28,
    bullThesis: 'Azure AI revenue growing 70%+ YoY. Copilot adoption driving Office 365 ARPU expansion. GitHub Copilot leader in developer AI.',
    bearThesis: 'Massive capex spend on AI infrastructure with uncertain ROI timeline. Antitrust scrutiny on Activision and OpenAI relationship.',
    catalysts: ['Copilot monetization', 'Azure AI growth'],
    risks: ['Capex ROI', 'OpenAI dependency'],
    sparkline: [370, 375, 380, 385, 390, 395, 400, 405, 408, 412, 414, 416],
    needsAttention: false,
    metrics: { pe: '35.8', pb: '12.1', ps: '13.4', evEbitda: '27.4', roe: '37.2%', divYield: '0.72%' },
  },
];

const TOTAL_VALUE = MOCK_HOLDINGS.reduce((s, h) => s + h.currentValue, 0);
const TOTAL_GAIN = MOCK_HOLDINGS.reduce((s, h) => s + h.gainLoss, 0);
const TOTAL_GAIN_PCT = (TOTAL_GAIN / (TOTAL_VALUE - TOTAL_GAIN)) * 100;
const TODAY_CHANGE = 342.18;
const TODAY_CHANGE_PCT = (TODAY_CHANGE / TOTAL_VALUE) * 100;

// ── Inline mini-components ──────────────────────────────────────────────

function MockSparkline({ data, positive }: { data: number[]; positive: boolean }) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 60;
  const h = 24;
  const points = data.map((v, i) =>
    `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`
  ).join(' ');

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0">
      <polyline
        points={points}
        fill="none"
        stroke={positive ? '#4ade80' : '#f87171'}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MockStanceGauge({ bullIntensity, bearRisk, stance }: { bullIntensity: number; bearRisk: number; stance: Stance }) {
  const labels: Record<Stance, string> = {
    BULLISH: 'Bullish',
    LEANING_BULLISH: 'Lean Bull',
    NEUTRAL: 'Neutral',
    LEANING_BEARISH: 'Lean Bear',
    BEARISH: 'Bearish',
  };
  const labelColors: Record<Stance, string> = {
    BULLISH: 'text-green-400',
    LEANING_BULLISH: 'text-green-300',
    NEUTRAL: 'text-text-secondary',
    LEANING_BEARISH: 'text-red-300',
    BEARISH: 'text-red-400',
  };

  const isBearish = stance === 'BEARISH' || stance === 'LEANING_BEARISH';
  const isBullish = stance === 'BULLISH' || stance === 'LEANING_BULLISH';

  // Mute the inactive side to reduce visual strain
  const bullColor = isBearish ? 'bg-surface-raised/40' : 'bg-green-500/60';
  const bearColor = isBullish ? 'bg-surface-raised/40' : 'bg-red-500/60';

  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="relative w-16 h-2 rounded-full bg-surface-light overflow-hidden">
        <div
          className={cn('absolute left-0 top-0 h-full rounded-l-full', bullColor)}
          style={{ width: `${bullIntensity}%` }}
        />
        <div
          className={cn('absolute right-0 top-0 h-full rounded-r-full', bearColor)}
          style={{ width: `${bearRisk}%` }}
        />
      </div>
      <span className={cn('text-[10px] font-medium whitespace-nowrap', labelColors[stance])}>
        {labels[stance]}
      </span>
    </div>
  );
}

function MetricItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-text-muted">{label}</p>
      <p className="text-xs text-text-primary font-medium tabular-nums">{value}</p>
    </div>
  );
}

function formatCurrency(val: number) {
  return val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatPrice(val: number) {
  return val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Main component ──────────────────────────────────────────────────────

export function PortfolioExample() {
  const [expandedTicker, setExpandedTicker] = useState<string | null>('NVDA');
  const flagged = MOCK_HOLDINGS.filter(h => h.needsAttention);

  return (
    <div className="relative">
      {/* Gradient border wrapper */}
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/10 via-white/5 to-transparent pointer-events-none" />

      <div className="relative rounded-2xl overflow-hidden bg-surface/80 border border-border-subtle p-4 sm:p-6 space-y-5">
        {/* Example data badge */}
        <div className="absolute top-3 right-3 z-10 px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider text-text-muted bg-surface-light/50 border border-border/30">
          Example Data
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-surface/60 border border-border p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-text-muted mb-1">Total Value</p>
            <p className="text-base sm:text-xl font-bold text-text-primary">{formatCurrency(TOTAL_VALUE)}</p>
          </div>
          <div className="rounded-xl bg-surface/60 border border-border p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-text-muted mb-1">Total Gain/Loss</p>
            <p className={cn('text-base sm:text-xl font-bold', TOTAL_GAIN >= 0 ? 'text-emerald-400' : 'text-red-400')}>
              {TOTAL_GAIN >= 0 ? '+' : ''}{formatCurrency(TOTAL_GAIN)}
              <span className="text-xs ml-1 font-normal opacity-70">({TOTAL_GAIN_PCT >= 0 ? '+' : ''}{TOTAL_GAIN_PCT.toFixed(1)}%)</span>
            </p>
          </div>
          <div className="rounded-xl bg-surface/60 border border-border p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-text-muted mb-1">Today&apos;s Change</p>
            <p className={cn('text-base sm:text-xl font-bold', TODAY_CHANGE >= 0 ? 'text-emerald-400' : 'text-red-400')}>
              {TODAY_CHANGE >= 0 ? '+' : ''}{formatCurrency(TODAY_CHANGE)}
              <span className="text-xs ml-1 font-normal opacity-70">({TODAY_CHANGE_PCT >= 0 ? '+' : ''}{TODAY_CHANGE_PCT.toFixed(2)}%)</span>
            </p>
          </div>
        </div>

        {/* Needs Attention */}
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
            <h3 className="text-xs sm:text-sm font-semibold text-red-400">Needs Attention ({flagged.length})</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {flagged.map(h => (
              <div
                key={h.symbol}
                className="flex items-center justify-between gap-3 rounded-lg bg-red-500/[0.03] border border-red-500/[0.08] px-3 py-2.5"
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-sm font-bold text-text-primary">{h.symbol}</span>
                  <span className={cn('text-xs font-medium', h.gainLossPct >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                    {h.gainLossPct >= 0 ? '+' : ''}{h.gainLossPct.toFixed(1)}%
                  </span>
                </div>
                <MockStanceGauge bullIntensity={h.bullIntensity} bearRisk={h.bearRisk} stance={h.stance} />
              </div>
            ))}
          </div>
        </div>

        {/* Holdings table */}
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[580px]">
            <thead>
              <tr className="bg-surface/50 text-[10px] sm:text-xs text-text-muted uppercase tracking-wider">
                <th className="text-left py-2.5 px-3 font-medium">Symbol</th>
                <th className="text-left py-2.5 px-2 font-medium hidden sm:table-cell">30D</th>
                <th className="text-right py-2.5 px-3 font-medium">Value</th>
                <th className="text-right py-2.5 px-3 font-medium">Gain/Loss</th>
                <th className="text-left py-2.5 px-3 font-medium">Stance</th>
                <th className="text-center py-2.5 px-2 font-medium w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {MOCK_HOLDINGS.map(h => {
                const isExpanded = expandedTicker === h.symbol;
                return (
                  <Fragment key={h.symbol}>
                    <tr
                      onClick={() => setExpandedTicker(isExpanded ? null : h.symbol)}
                      className="cursor-pointer hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-text-primary">{h.symbol}</span>
                          <span className="text-[10px] text-text-muted hidden md:inline">{h.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 hidden sm:table-cell">
                        <MockSparkline data={h.sparkline} positive={h.gainLossPct >= 0} />
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className="text-xs sm:text-sm text-text-primary tabular-nums">{formatCurrency(h.currentValue)}</span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className={cn(
                          'text-xs sm:text-sm font-medium tabular-nums',
                          h.gainLossPct >= 0 ? 'text-green-400' : 'text-red-400'
                        )}>
                          {h.gainLossPct >= 0 ? '+' : ''}{h.gainLossPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <MockStanceGauge bullIntensity={h.bullIntensity} bearRisk={h.bearRisk} stance={h.stance} />
                      </td>
                      <td className="py-3 px-2 text-center">
                        <ChevronDown className={cn(
                          'w-4 h-4 text-text-muted transition-transform mx-auto',
                          isExpanded && 'rotate-180'
                        )} />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <div className="px-4 py-4 bg-surface/30 space-y-4">
                            {/* Position summary */}
                            <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
                              <div>
                                <p className="text-[10px] text-text-muted uppercase tracking-wider">Shares</p>
                                <p className="text-sm text-text-primary font-semibold tabular-nums">{h.quantity}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-muted uppercase tracking-wider">Avg Cost</p>
                                <p className="text-sm text-text-primary font-semibold tabular-nums">{formatPrice(h.avgCost)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-muted uppercase tracking-wider">Price</p>
                                <p className="text-sm text-text-primary font-semibold tabular-nums">{formatPrice(h.currentPrice)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-muted uppercase tracking-wider">Value</p>
                                <p className="text-sm text-text-primary font-semibold tabular-nums">{formatCurrency(h.currentValue)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-text-muted uppercase tracking-wider">P&amp;L</p>
                                <p className={cn(
                                  'text-sm font-semibold tabular-nums',
                                  h.gainLoss >= 0 ? 'text-green-400' : 'text-red-400'
                                )}>
                                  {h.gainLoss >= 0 ? '+' : ''}{formatCurrency(h.gainLoss)} ({h.gainLossPct >= 0 ? '+' : ''}{h.gainLossPct.toFixed(1)}%)
                                </p>
                              </div>
                            </div>

                            {/* Bull vs Bear thesis */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="rounded-lg bg-green-500/5 border border-green-500/10 p-3">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                                  <span className="text-xs font-semibold text-green-400">Bull Case</span>
                                </div>
                                <p className="text-xs text-text-secondary leading-relaxed">{h.bullThesis}</p>
                              </div>
                              <div className="rounded-lg bg-red-500/5 border border-red-500/10 p-3">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                                  <span className="text-xs font-semibold text-red-400">Bear Case</span>
                                </div>
                                <p className="text-xs text-text-secondary leading-relaxed">{h.bearThesis}</p>
                              </div>
                            </div>

                            {/* Catalyst + Risk chips */}
                            <div className="flex flex-wrap gap-2">
                              {h.catalysts.map(c => (
                                <span key={c} className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/15">
                                  <Zap className="w-2.5 h-2.5" />
                                  {c}
                                </span>
                              ))}
                              {h.risks.map(r => (
                                <span key={r} className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/15">
                                  <ShieldAlert className="w-2.5 h-2.5" />
                                  {r}
                                </span>
                              ))}
                            </div>

                            {/* Key Metrics */}
                            <div>
                              <h4 className="text-[10px] text-text-muted uppercase tracking-wider font-semibold mb-2">Key Metrics</h4>
                              <div className="grid grid-cols-3 sm:grid-cols-6 gap-x-4 gap-y-1.5">
                                <MetricItem label="P/E" value={h.metrics.pe} />
                                <MetricItem label="P/B" value={h.metrics.pb} />
                                <MetricItem label="P/S" value={h.metrics.ps} />
                                <MetricItem label="EV/EBITDA" value={h.metrics.evEbitda} />
                                <MetricItem label="ROE" value={h.metrics.roe} />
                                <MetricItem label="Div Yield" value={h.metrics.divYield} />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
